/**
 * <kc-gallery-view-toggle> — blocks/kc-gallery-view-toggle.liquid.
 *
 * Pills over the product gallery ("Sofa" / "Als Bett ansehen") that jump to a
 * specific gallery image.
 *
 * Which image: the pill's image number (data-fallback-index), which is not
 * translatable content and so behaves the same in every language. Optionally
 * (block setting "Find the image by alt text") a keyword is tried first — off
 * by default, because translation apps translate the keyword too and it then
 * matched the wrong photo (FR "canapé-lit" contains both "canapé" and "lit").
 * With neither, the pill hides itself rather than jumping somewhere arbitrary.
 *
 * How it jumps: by clicking the matching thumbnail, i.e. the same path a
 * shopper takes, so the carousel, dots and zoom stay in sync. Thumbnails are
 * rendered in the same order as the slides (snippets/kc-product-gallery.liquid),
 * so one index addresses both.
 */

class KcGalleryViewToggle extends HTMLElement {
  connectedCallback() {
    // Moving the element into the gallery (moveIntoGallery) disconnects and
    // reconnects it, which re-enters this callback mid-setup. Without the guard
    // that nested run built a second counter and bound every pill twice.
    if (this.initialized) return;
    this.initialized = true;

    this.gallery = this.closest(".product-gallery") || document.querySelector(".product-gallery");
    if (!this.gallery) return;

    if (this.hasAttribute("overlay")) {
      this.moveIntoGallery();
      if (this.hasAttribute("counter")) this.createCounter();
      if (this.hasAttribute("align-zoom")) this.alignWithZoom();
    }

    this.pills = [...this.querySelectorAll("[data-view-pill]")];
    this.resolveTargets();
    this.pills.forEach((pill) => pill.addEventListener("click", () => this.activate(pill)));
    this.watchCurrentSlide();
    this.syncActivePill();
  }

  disconnectedCallback() {
    // The move into the gallery is a disconnect too; only tear down when the
    // element has really left the page (e.g. a section re-render replaced it).
    if (this.isConnected) return;
    queueMicrotask(() => {
      if (this.isConnected) return;
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.counter?.remove();
    });
  }

  /**
   * Centre the pills on the zoom button's line, so the two controls at the
   * bottom of the gallery read as one row. The zoom button's size and offset
   * differ per breakpoint, so this is measured, not a fixed number, and
   * re-measured whenever the gallery resizes.
   */
  alignWithZoom() {
    const wrapper = this.parentElement;
    const zoom = wrapper?.querySelector(".product-gallery__zoom button, .product-gallery__zoom");
    if (!zoom) return;

    const place = () => {
      const wrapperRect = wrapper.getBoundingClientRect();
      const zoomRect = zoom.getBoundingClientRect();
      if (!zoomRect.height) {
        // No zoom button at this breakpoint (hidden on phones per template):
        // drop the measured offsets so the stylesheet's own insets apply.
        this.style.bottom = "";
        this.style.right = "";
        if (this.counter) this.counter.style.bottom = "";
        return;
      }
      const zoomCentreFromBottom = wrapperRect.bottom - zoomRect.bottom + zoomRect.height / 2;
      this.style.bottom = `${Math.round(zoomCentreFromBottom - this.offsetHeight / 2)}px`;
      if (this.counter?.offsetHeight) {
        this.counter.style.bottom = `${Math.round(zoomCentreFromBottom - this.counter.offsetHeight / 2)}px`;
      }
      // Right-aligned: park the pills just left of the zoom button instead of
      // under it. Measured, because the button's size and inset differ per
      // breakpoint. Only when the button is actually on the right — it can sit
      // bottom-left (or be hidden) per template, and then the pills keep the
      // plain inset from the stylesheet.
      if (this.dataset.align === "right") {
        const phone = window.matchMedia("(max-width: 749px)").matches;
        const zoomOnTheRight = zoomRect.left > wrapperRect.left + wrapperRect.width / 2;
        // Measured against whatever element actually positions the pills: if
        // the move into the gallery did not happen, the stylesheet's inset
        // would be read from a wider ancestor and the pills would hang over
        // the photo's edge.
        const anchorRect = (this.offsetParent || wrapper).getBoundingClientRect();
        const edge = anchorRect.right - wrapperRect.right;
        const inset = zoomOnTheRight
          ? wrapperRect.right - zoomRect.left + (phone ? 8 : 12) // clear of the button
          : phone
            ? 10
            : 16; // same inset as the wishlist heart
        this.style.right = `${Math.round(edge + inset)}px`;
      }
    };

    place();
    this.resizeObserver = new ResizeObserver(place);
    this.resizeObserver.observe(wrapper);
  }

  /** The pills are authored after the gallery; visually they belong on top of it. */
  moveIntoGallery() {
    const wrapper = this.gallery.querySelector(".product-gallery__media-list-wrapper");
    if (wrapper && !wrapper.contains(this)) wrapper.appendChild(this);
  }

  get slides() {
    return [...this.gallery.querySelectorAll("media-carousel [data-media-id]")];
  }

  get thumbnails() {
    return [...this.gallery.querySelectorAll(".product-gallery__thumbnail")];
  }

  resolveTargets() {
    const slides = this.slides;

    this.pills.forEach((pill) => {
      const keyword = (pill.dataset.keyword || "").trim().toLowerCase();
      let index = -1;

      if (keyword) {
        index = slides.findIndex((slide) => {
          const img = slide.querySelector("img");
          const text = `${img?.getAttribute("alt") || ""} ${slide.getAttribute("data-media-alt") || ""}`;
          return text.toLowerCase().includes(keyword);
        });
      }

      // Fallback: the image number from the editor, 1-based.
      if (index < 0) {
        const fallback = parseInt(pill.dataset.fallbackIndex || "0", 10);
        if (fallback > 0 && fallback <= slides.length) index = fallback - 1;
      }

      if (index < 0) {
        pill.hidden = true;
        return;
      }

      pill.dataset.targetIndex = String(index);
    });

    // A single usable pill is not a choice — hide the whole control.
    if (this.pills.filter((pill) => !pill.hidden).length < 2) this.hidden = true;
  }

  activate(pill) {
    const index = parseInt(pill.dataset.targetIndex ?? "-1", 10);
    if (index < 0) return;

    const thumbnail = this.thumbnails[index];
    if (thumbnail) {
      thumbnail.click();
    } else {
      this.slides[index]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }

    this.setActive(pill);
  }

  setActive(activePill) {
    this.pills.forEach((pill) => {
      const isActive = pill === activePill;
      pill.classList.toggle("is-active", isActive);
      pill.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  }

  /** Keep the pills honest when the shopper swipes or uses the thumbnails. */
  watchCurrentSlide() {
    const list = this.gallery.querySelector(".product-gallery__thumbnail-list");
    if (!list) return;

    this.observer = new MutationObserver(() => this.syncActivePill());
    this.observer.observe(list, { attributes: true, attributeFilter: ["aria-current"], subtree: true });
  }

  /**
   * Phones: an "n/total" counter in the gallery's bottom-left corner. It stands
   * in for the dot row (the thumbnail list styled as dots), which the block's
   * CSS hides because it ran underneath the pills.
   */
  createCounter() {
    const wrapper = this.parentElement;
    if (!wrapper || this.thumbnails.length < 2) return;
    this.counter = document.createElement("span");
    // Left-aligned pills own the bottom-left corner, so the counter centres.
    this.counter.className =
      this.dataset.align === "left" ? "kc-gallery-counter kc-gallery-counter--centered" : "kc-gallery-counter";
    this.counter.setAttribute("aria-hidden", "true");
    // Text before insertion, so alignWithZoom measures the real height.
    const current = Math.max(0, this.thumbnails.findIndex((thumb) => thumb.getAttribute("aria-current") === "true"));
    this.counter.textContent = `${current + 1}/${this.thumbnails.length}`;
    wrapper.appendChild(this.counter);
    this.gallery.classList.add("has-kc-gallery-counter");
  }

  syncActivePill() {
    const current = this.thumbnails.findIndex((thumb) => thumb.getAttribute("aria-current") === "true");
    if (current < 0) return;

    if (this.counter) this.counter.textContent = `${current + 1}/${this.thumbnails.length}`;

    const match = this.pills.find((pill) => parseInt(pill.dataset.targetIndex ?? "-1", 10) === current);
    this.setActive(match || null);
  }
}

if (!customElements.get("kc-gallery-view-toggle")) {
  customElements.define("kc-gallery-view-toggle", KcGalleryViewToggle);
}
